import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { Fragment, type ReactNode } from 'react';
import { CareEnvironment } from '@shared/appModel';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import type { StrategyPresentation } from './types';

// OneAmerica AnnuityCare II (two-pool annuity hybrid). The LTCAV pool is drawn
// first (24-month period, cap = scheduled LTCAV/24); the COB pool engages only
// once the LTCAV is exhausted (72-month period, cap = COB/72 inflating 5%). The
// death-benefit cell carries the annuity Accumulated Value floor, depleted by
// LTCAV draws only. Mirrors oaAssetCarePresentation's two-pool conventions; all
// monthly-table and pre-care values (incl. the annuity AV) are read off the
// engine's precomputed month state and YearSnapshots.

const DEFAULT_LTCAV_MONTHS = 24;
const DEFAULT_COB_MONTHS = 72;
const SCHEDULE_MATCH_TOLERANCE = 2;

// Pre-rate-model rows (P2-29) omit the LTCAV benefit period from terms: their
// base cap is the legacy daily-field cap, NOT pool ÷ period, so every
// "LTCAV ÷ N months" derivation must be suppressed for them (the stored pool
// dollar is the old prompt's Total LTC Balance). The COB cap is period-derived
// in every mode, so COB derivations stay.
function hasLtcavPeriodDerivedCap(terms: ClaimTerms): boolean {
  return terms.baseBenefitPoolDurationMonths != null;
}

const isAllZero = (timeline: AccumulationYear[], pick: (s: YearSnapshot) => number): boolean =>
  timeline.every(row => pick(row.snapshot) <= 0.5);

function normalizedPolicyYear(value: number): number {
  return Math.max(Math.floor(value), 1);
}

function isNearCurrency(actual: number, expected: number): boolean {
  return Math.abs(actual - expected) <= SCHEDULE_MATCH_TOLERANCE;
}

function formatFactor(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(3).replace(/\.?0+$/, '')}%`;
}

function hasLtcavScheduleInputs(terms: ClaimTerms): boolean {
  return (
    terms.baseBenefitPoolAtIssue != null &&
    terms.baseBenefitGuaranteedInitialRate != null &&
    terms.baseBenefitGuaranteedInitialRateYears != null &&
    terms.baseBenefitGuaranteedRenewalRate != null &&
    terms.monthlyContinuationBenefitChargeRate != null
  );
}

function hasAccumulatedValueScheduleInputs(terms: ClaimTerms): boolean {
  return (
    hasLtcavScheduleInputs(terms) &&
    terms.accumulatedValueAtIssue != null &&
    terms.accumulatedValueGuaranteedInitialRate != null &&
    terms.accumulatedValueGuaranteedInitialRateYears != null &&
    terms.accumulatedValueGuaranteedRenewalRate != null
  );
}

// Annuity Accumulated Value for a pre-care row. The engine computes the AV on
// the same guaranteed schedule and stores it on the YearSnapshot, so we read it
// straight off the snapshot (no client-side re-derivation). Falls back to the
// at-issue value for older snapshots / configs that don't carry the schedule.
function snapshotAccumulatedValue(snapshot: YearSnapshot, terms: ClaimTerms): number {
  return (
    snapshot.accumulatedValue ??
    terms.accumulatedValueAtIssue ??
    terms.minimumGuaranteedDeathBenefit ??
    0
  );
}

function ltcavScheduleDriver(terms: ClaimTerms): string {
  if (!hasLtcavScheduleInputs(terms)) {
    return 'Stored LTCAV when the carrier schedule is unavailable';
  }
  return `Guaranteed LTCAV rates (${formatPercent(
    terms.baseBenefitGuaranteedInitialRate!,
  )} for ${terms.baseBenefitGuaranteedInitialRateYears} yr, then ${formatPercent(
    terms.baseBenefitGuaranteedRenewalRate!,
  )}) less the ${formatPercent(terms.monthlyContinuationBenefitChargeRate!)} monthly COB rider charge`;
}

function accumulatedValueScheduleDriver(terms: ClaimTerms): string {
  if (!hasAccumulatedValueScheduleInputs(terms)) {
    return 'Stored Accumulated Value when the carrier AV schedule is unavailable';
  }
  return `Guaranteed AV rates (${formatPercent(
    terms.accumulatedValueGuaranteedInitialRate!,
  )} for ${terms.accumulatedValueGuaranteedInitialRateYears} yr, then ${formatPercent(
    terms.accumulatedValueGuaranteedRenewalRate!,
  )}) less the monthly COB rider charge based on LTCAV`;
}

function cobScheduleDriver(terms: ClaimTerms): string {
  const regularRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  if (terms.continuationBenefitFirstAnniversaryRate != null) {
    return `Year-1 COB, then ${formatPercent(
      terms.continuationBenefitFirstAnniversaryRate,
    )} first-anniversary COB step and ${formatPercent(regularRate)} later COB growth`;
  }
  return `Year-1 COB compounded at ${formatPercent(regularRate)} COB growth`;
}

function scheduleYearsAfterIssue(
  policyYear: number,
  initialRateYears: number,
): { yearsAfterIssue: number; initialYears: number; renewalYears: number; monthlySteps: number } {
  const yearsAfterIssue = Math.max(policyYear - 1, 0);
  const initialYears = Math.min(yearsAfterIssue, Math.max(initialRateYears - 1, 0));
  const renewalYears = Math.max(yearsAfterIssue - initialYears, 0);
  return {
    yearsAfterIssue,
    initialYears,
    renewalYears,
    monthlySteps: yearsAfterIssue * 12,
  };
}

function cappedGrowthYears(rawYears: number, capYears: number | undefined): number {
  if (capYears && capYears > 0) return Math.min(rawYears, capYears);
  return rawYears;
}

function cappedCobRegularGrowthYears(policyYear: number, capYears: number | undefined): number {
  const rawYears = Math.max(policyYear - 2, 0);
  if (capYears && capYears > 0) return Math.min(rawYears, Math.max(capYears - 1, 0));
  return rawYears;
}

function ScheduleNote({
  title,
  rows,
  detail,
}: {
  title: string;
  rows: { label: string; value: ReactNode }[];
  detail?: ReactNode;
}) {
  return (
    <div className="mt-1 rounded border border-gray-700/70 bg-gray-950/40 px-2 py-1.5">
      <div className="mb-1 font-medium text-gray-200">{title}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        {rows.map(row => (
          <Fragment key={row.label}>
            <span className="text-gray-400">{row.label}</span>
            <span className="text-right font-semibold tabular-nums text-gray-100">{row.value}</span>
          </Fragment>
        ))}
      </div>
      {detail && (
        <div className="mt-1 border-t border-gray-700/70 pt-1 text-gray-400">{detail}</div>
      )}
    </div>
  );
}

function AccumulationValueTooltip({
  title,
  rows,
  detail,
}: {
  title: string;
  rows: { label: string; value: ReactNode }[];
  detail?: ReactNode;
}) {
  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">{title}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        {rows.map(row => (
          <Fragment key={row.label}>
            <span className="text-gray-300">{row.label}</span>
            <span className="font-semibold tabular-nums">{row.value}</span>
          </Fragment>
        ))}
      </div>
      {detail && (
        <div className="mt-1 border-t border-gray-700 pt-1 text-[10px] text-gray-300">{detail}</div>
      )}
    </TooltipContent>
  );
}

function ltcavAccumulationTooltip(row: AccumulationYear, terms: ClaimTerms): ReactNode {
  return (
    <AccumulationValueTooltip
      title={`Policy year ${row.policyYear} · LTCAV`}
      rows={[
        {
          label: `Year-1 LTCAV (${terms.policyPremiumStartYear})`,
          value: currency(terms.baseBenefitPoolAtIssue ?? row.snapshot.effectiveFaceAmount),
        },
        { label: 'This year', value: currency(row.snapshot.effectiveFaceAmount) },
        { label: 'Driver', value: ltcavScheduleDriver(terms) },
      ]}
      detail="LTCAV is the base LTC pool used first when care begins."
    />
  );
}

function cobAccumulationTooltip(row: AccumulationYear, terms: ClaimTerms): ReactNode {
  const atIssue = terms.extensionOfBenefitsPoolAtIssue ?? row.snapshot.effectiveContinuationBenefit;
  const regularRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const capYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  const firstAnniversaryRate = terms.continuationBenefitFirstAnniversaryRate;
  const policyYear = normalizedPolicyYear(row.policyYear);
  const growthRows: { label: string; value: ReactNode }[] = [
    { label: `Year-1 COB (${terms.policyPremiumStartYear})`, value: currency(atIssue) },
  ];

  if (policyYear > 1 && firstAnniversaryRate != null) {
    growthRows.push({ label: 'First step', value: formatFactor(1 + firstAnniversaryRate) });
    growthRows.push({
      label: 'Later growth',
      value: `${formatFactor(1 + regularRate)}^${cappedCobRegularGrowthYears(policyYear, capYears)}`,
    });
  } else if (policyYear > 1) {
    growthRows.push({
      label: 'Growth',
      value: `${formatFactor(1 + regularRate)}^${cappedGrowthYears(policyYear - 1, capYears)}`,
    });
  }

  growthRows.push({
    label: 'This year',
    value: currency(row.snapshot.effectiveContinuationBenefit),
  });
  growthRows.push({ label: 'Driver', value: cobScheduleDriver(terms) });

  return (
    <AccumulationValueTooltip
      title={`Policy year ${row.policyYear} · COB balance`}
      rows={growthRows}
      detail="COB is separate from the Accumulated Value and is used after LTCAV is exhausted."
    />
  );
}

function totalLtcAccumulationTooltip(row: AccumulationYear): ReactNode {
  return (
    <AccumulationValueTooltip
      title={`Policy year ${row.policyYear} · total scheduled LTC`}
      rows={[
        { label: 'LTCAV', value: currency(row.snapshot.effectiveFaceAmount) },
        { label: 'COB balance', value: currency(row.snapshot.effectiveContinuationBenefit) },
        { label: 'Total scheduled LTC', value: currency(row.snapshot.theoreticalLtcPoolCeiling) },
      ]}
      detail="Total scheduled LTC is the LTCAV plus COB balance before any benefits are used."
    />
  );
}

function accumulatedValueAccumulationTooltip(row: AccumulationYear, terms: ClaimTerms): ReactNode {
  return (
    <AccumulationValueTooltip
      title={`Policy year ${row.policyYear} · Accumulated Value`}
      rows={[
        {
          label: `Year-1 AV (${terms.policyPremiumStartYear})`,
          value: currency(
            terms.accumulatedValueAtIssue ?? terms.minimumGuaranteedDeathBenefit ?? 0,
          ),
        },
        { label: 'This year', value: currency(snapshotAccumulatedValue(row.snapshot, terms)) },
        { label: 'Driver', value: accumulatedValueScheduleDriver(terms) },
      ]}
      detail="Later LTCAV withdrawals reduce this residual value; COB withdrawals do not."
    />
  );
}

function monthlyBenefitAccumulationTooltip(row: AccumulationYear, terms: ClaimTerms): ReactNode {
  const ltcavMonths = terms.baseBenefitPoolDurationMonths ?? DEFAULT_LTCAV_MONTHS;
  const cobMonths = terms.extensionOfBenefitsPoolDurationMonths ?? DEFAULT_COB_MONTHS;
  const cobMonthly = row.snapshot.continuationMonthlyBenefitCap ?? 0;

  return (
    <AccumulationValueTooltip
      title={`Policy year ${row.policyYear} · monthly benefit`}
      rows={[
        {
          label: 'LTCAV limit',
          value: hasLtcavPeriodDerivedCap(terms)
            ? `${currency(row.snapshot.effectiveFaceAmount)} ÷ ${ltcavMonths} = ${currency(
                row.snapshot.monthlyBenefitCap,
              )}`
            : `${currency(row.snapshot.monthlyBenefitCap)} (policy's stated monthly limit)`,
        },
        {
          label: 'COB limit',
          value: `${currency(row.snapshot.effectiveContinuationBenefit)} ÷ ${cobMonths} = ${currency(
            cobMonthly,
          )}`,
        },
      ]}
      detail="The displayed monthly benefit is the LTCAV limit. COB has its own limit after LTCAV is exhausted."
    />
  );
}

function buildAnnuityCareTwoAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const hasLtcavSchedule = hasLtcavScheduleInputs(terms);
  const hasAvSchedule = hasAccumulatedValueScheduleInputs(terms);
  const hasFirstAnniversaryCobStep = terms.continuationBenefitFirstAnniversaryRate != null;
  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  if (!isAllZero(timeline, s => s.effectiveFaceAmount)) {
    columns.push({
      key: 'ltcav',
      label: 'LTCAV',
      tooltip: (
        <HeaderTooltip
          description="The LTC Accumulated Value (LTCAV), the base LTC pool used first when care begins."
          formulas={[
            {
              label: 'Driver',
              expression: hasLtcavSchedule
                ? 'year-1 LTCAV + guaranteed LTCAV rates − monthly COB rider charge'
                : 'stored LTCAV',
            },
          ]}
          notes={
            hasAvSchedule
              ? 'The COB rider charge is applied through AV and LTCAV is adjusted proportionally.'
              : hasLtcavSchedule
                ? 'The monthly COB rider charge directly reduces scheduled LTCAV.'
                : 'When the carrier schedule is unavailable, stored LTCAV carries forward before care.'
          }
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={ltcavAccumulationTooltip(row, terms)}
        />
      ),
    });
  }

  if (!isAllZero(timeline, s => s.effectiveContinuationBenefit)) {
    columns.push({
      key: 'cob-balance',
      label: 'COB balance',
      tooltip: (
        <HeaderTooltip
          description="The Continuation of Benefits balance, used after LTCAV is exhausted."
          formulas={[
            {
              label: 'Driver',
              expression: hasFirstAnniversaryCobStep
                ? 'year-1 COB × first-anniversary COB step × later COB growth'
                : 'stored COB × COB growth',
            },
            { label: 'Draw order', expression: 'COB begins after LTCAV reaches $0' },
          ]}
          notes="COB is separate from the annuity Accumulated Value."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveContinuationBenefit}
          prevValue={prev?.snapshot.effectiveContinuationBenefit ?? null}
          tooltip={cobAccumulationTooltip(row, terms)}
        />
      ),
    });
  }

  if (!isAllZero(timeline, s => s.theoreticalLtcPoolCeiling)) {
    columns.push({
      key: 'total-ltc',
      label: 'Total LTC',
      tooltip: (
        <HeaderTooltip
          description="Total scheduled long-term-care dollars before benefits are used."
          formulas={[{ label: 'Total', expression: 'scheduled LTCAV + scheduled COB balance' }]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.theoreticalLtcPoolCeiling}
          prevValue={prev?.snapshot.theoreticalLtcPoolCeiling ?? null}
          tooltip={totalLtcAccumulationTooltip(row)}
        />
      ),
    });
  }

  if ((terms.accumulatedValueAtIssue ?? terms.minimumGuaranteedDeathBenefit ?? 0) > 0.5) {
    columns.push({
      key: 'accumulated-value',
      label: 'Accumulated value',
      tooltip: (
        <HeaderTooltip
          description="The annuity Accumulated Value before LTC withdrawals."
          formulas={[
            {
              label: 'Driver',
              expression: hasAvSchedule
                ? 'guaranteed AV rates − monthly COB rider charge based on LTCAV'
                : 'stored Accumulated Value',
            },
            {
              label: 'Later claim',
              expression: 'LTCAV withdrawals reduce AV; COB withdrawals do not',
            },
          ]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={snapshotAccumulatedValue(row.snapshot, terms)}
          prevValue={prev ? snapshotAccumulatedValue(prev.snapshot, terms) : null}
          tooltip={accumulatedValueAccumulationTooltip(row, terms)}
        />
      ),
    });
  }

  if (!isAllZero(timeline, s => s.monthlyBenefitCap)) {
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="The LTCAV monthly maximum before care begins. COB has a separate monthly maximum after LTCAV is exhausted."
          formulas={[
            {
              label: 'LTCAV limit',
              expression: hasLtcavPeriodDerivedCap(terms)
                ? 'scheduled LTCAV ÷ LTCAV benefit period'
                : "policy's stated monthly limit (pre-rate-model row)",
            },
            { label: 'COB limit', expression: 'scheduled COB balance ÷ COB benefit period' },
          ]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={monthlyBenefitAccumulationTooltip(row, terms)}
        />
      ),
    });
  }

  return columns;
}

function ltcavOpeningScheduleNote({
  month,
  openBase,
  terms,
}: {
  month: MonthlyEntry;
  openBase: number;
  terms: ClaimTerms;
}): ReactNode | undefined {
  const atIssue = terms.baseBenefitPoolAtIssue;
  if (!atIssue || openBase <= 0 || month.facts.appliedCapPoolSource !== 'base') {
    return undefined;
  }

  const months = terms.baseBenefitPoolDurationMonths ?? DEFAULT_LTCAV_MONTHS;
  const scheduledBase = month.facts.appliedMonthlyBenefitCap * months;
  if (!isNearCurrency(openBase, scheduledBase)) return undefined;

  const factor = openBase / atIssue;
  const policyYear = normalizedPolicyYear(month.context.policyYear);
  const hasRateInputs =
    terms.baseBenefitGuaranteedInitialRate != null &&
    terms.baseBenefitGuaranteedInitialRateYears != null &&
    terms.baseBenefitGuaranteedRenewalRate != null &&
    terms.monthlyContinuationBenefitChargeRate != null;
  const hasAvInputs =
    terms.accumulatedValueAtIssue != null &&
    terms.accumulatedValueGuaranteedInitialRate != null &&
    terms.accumulatedValueGuaranteedInitialRateYears != null &&
    terms.accumulatedValueGuaranteedRenewalRate != null;

  if (!hasRateInputs) {
    return (
      <ScheduleNote
        title={`Opening schedule · PY ${policyYear}`}
        rows={[
          { label: 'Year-1 LTCAV', value: currency(atIssue) },
          { label: 'Factor', value: formatFactor(factor) },
          { label: 'Opening', value: currency(openBase) },
        ]}
      />
    );
  }

  const ltcavYears = scheduleYearsAfterIssue(
    policyYear,
    terms.baseBenefitGuaranteedInitialRateYears!,
  );
  const avYears = hasAvInputs
    ? scheduleYearsAfterIssue(policyYear, terms.accumulatedValueGuaranteedInitialRateYears!)
    : null;

  return (
    <ScheduleNote
      title={`Opening schedule · PY ${policyYear}`}
      rows={[
        { label: 'Year-1 LTCAV', value: currency(atIssue) },
        { label: 'Factor', value: formatFactor(factor) },
        { label: 'Opening', value: currency(openBase) },
      ]}
      detail={
        <>
          {ltcavYears.initialYears} yr at {formatPercent(terms.baseBenefitGuaranteedInitialRate!)},
          then {ltcavYears.renewalYears} yr at{' '}
          {formatPercent(terms.baseBenefitGuaranteedRenewalRate!)}; monthly COB charge{' '}
          {formatPercent(terms.monthlyContinuationBenefitChargeRate!)}
          {hasAvInputs && avYears
            ? `; AV uses ${avYears.initialYears} yr at ${formatPercent(
                terms.accumulatedValueGuaranteedInitialRate!,
              )}, then ${avYears.renewalYears} yr at ${formatPercent(
                terms.accumulatedValueGuaranteedRenewalRate!,
              )}`
            : ''}
          .
        </>
      }
    />
  );
}

function cobOpeningScheduleNote({
  month,
  openEob,
  terms,
}: {
  month: MonthlyEntry;
  openEob: number;
  terms: ClaimTerms;
}): ReactNode | undefined {
  const atIssue = terms.extensionOfBenefitsPoolAtIssue;
  const regularRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  if (!atIssue || openEob <= 0 || regularRate == null) return undefined;

  const policyYear = normalizedPolicyYear(month.context.policyYear);
  const firstAnniversaryRate = terms.continuationBenefitFirstAnniversaryRate;
  const capYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;

  if (policyYear <= 1) {
    return isNearCurrency(openEob, atIssue) ? (
      <ScheduleNote
        title="Opening schedule"
        rows={[{ label: 'Year-1 COB', value: currency(atIssue) }]}
      />
    ) : undefined;
  }

  if (firstAnniversaryRate != null) {
    const regularYears = cappedCobRegularGrowthYears(policyYear, capYears);
    const expected = atIssue * (1 + firstAnniversaryRate) * Math.pow(1 + regularRate, regularYears);
    if (!isNearCurrency(openEob, expected)) return undefined;

    return (
      <ScheduleNote
        title={`Opening schedule · PY ${policyYear}`}
        rows={[
          { label: 'Year-1 COB', value: currency(atIssue) },
          { label: 'First step', value: formatFactor(1 + firstAnniversaryRate) },
          { label: 'Later growth', value: `${formatFactor(1 + regularRate)}^${regularYears}` },
          { label: 'Opening', value: currency(openEob) },
        ]}
        detail={`${formatPercent(regularRate)} COB growth after the first anniversary step.`}
      />
    );
  }

  const growthYears = cappedGrowthYears(Math.max(policyYear - 1, 0), capYears);
  const expected = atIssue * Math.pow(1 + regularRate, growthYears);
  if (!isNearCurrency(openEob, expected)) return undefined;

  return (
    <ScheduleNote
      title={`Opening schedule · PY ${policyYear}`}
      rows={[
        { label: 'Year-1 COB', value: currency(atIssue) },
        { label: 'Growth', value: `${formatFactor(1 + regularRate)}^${growthYears}` },
        { label: 'Opening', value: currency(openEob) },
      ]}
      detail={`${formatPercent(regularRate)} COB growth.`}
    />
  );
}

function ScheduleLimitTooltip({
  title,
  scheduledPool,
  durationMonths,
  monthlyCap,
  note,
}: {
  title: string;
  scheduledPool: number;
  durationMonths: number;
  monthlyCap: number;
  note: string;
}) {
  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">{title}</div>
      <div className="mb-1 text-[10px] text-gray-300">{note}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Scheduled pool</span>
        <span className="font-semibold tabular-nums">{currency(scheduledPool)}</span>
        <span className="text-gray-300">Benefit period</span>
        <span className="font-semibold tabular-nums">{durationMonths} mo</span>
        <span className="text-gray-300">Monthly cap</span>
        <span className="font-semibold tabular-nums">
          {currency(scheduledPool)} ÷ {durationMonths} = {currency(monthlyCap)}
        </span>
        <span className="text-gray-300">Daily equivalent</span>
        <span className="font-semibold tabular-nums">
          {currency(monthlyCap / DAYS_IN_MONTH)}/day
        </span>
      </div>
    </TooltipContent>
  );
}

function AnnuityCareTwoPoolLimitCell({
  snapshot,
  terms,
}: {
  snapshot: YearSnapshot | undefined;
  terms: ClaimTerms;
}) {
  if (!snapshot) return <span className="text-gray-300">—</span>;

  const policyYear = Math.floor(snapshot.policyYear);
  const ltcav = snapshot.effectiveFaceAmount;
  const cob = snapshot.effectiveContinuationBenefit ?? 0;
  const total = snapshot.theoreticalLtcPoolCeiling;
  const ltcavMonths = terms.baseBenefitPoolDurationMonths ?? DEFAULT_LTCAV_MONTHS;
  const cobMonths = terms.extensionOfBenefitsPoolDurationMonths ?? DEFAULT_COB_MONTHS;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help leading-tight">
          <div className="font-medium text-gray-900">{currency(total)}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Scheduled pool · policy year {policyYear}</div>
        <div className="mb-1 text-[10px] text-gray-300">
          Spend-independent carrier schedule for this policy year. Pool remaining is this schedule
          minus actual draws, plus any anniversary accruals while a pool remains open.
        </div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="col-span-2 mt-1 text-[10px] uppercase tracking-wide text-gray-400">
            LTC Accumulated Value
          </span>
          <span className="text-gray-300">Scheduled LTCAV</span>
          <span className="font-semibold tabular-nums">{currency(ltcav)}</span>
          <span className="text-gray-300">Monthly cap</span>
          <span className="font-semibold tabular-nums">
            {hasLtcavPeriodDerivedCap(terms)
              ? `${currency(ltcav)} ÷ ${ltcavMonths} = ${currency(ltcav / ltcavMonths)}`
              : currency(snapshot.monthlyBenefitCap)}
          </span>
          <span className="col-span-2 mt-1 text-[10px] uppercase tracking-wide text-gray-400">
            Continuation of Benefits
          </span>
          <span className="text-gray-300">Scheduled COB</span>
          <span className="font-semibold tabular-nums">{currency(cob)}</span>
          <span className="text-gray-300">Monthly cap</span>
          <span className="font-semibold tabular-nums">
            {currency(cob)} ÷ {cobMonths} = {currency(cob / cobMonths)}
          </span>
          <span className="mt-1 font-semibold text-gray-200">Total scheduled LTC</span>
          <span className="mt-1 font-semibold tabular-nums">{currency(total)}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export const annuityCareTwoPresentation: StrategyPresentation = {
  accumulationColumns: buildAnnuityCareTwoAccumulationColumns,

  // Headline the engine's own applied cap (LTCAV cap while LTCAV has balance,
  // COB cap once the draw moves to COB). The tooltip explains the scheduled
  // pool ÷ benefit-period relationship without re-deriving the carrier schedule.
  benefitAmountCell: ({ month, terms }) => {
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const fromCob = month.facts.appliedCapPoolSource === 'eob';
    const poolLabel = fromCob ? 'COB' : 'LTCAV';
    const intYear = Math.floor(month.context.policyYear);
    // Pre-rate-model rows have no period-derived LTCAV cap (P2-29) — show the
    // engine's cap without reconstructing a "pool ÷ months" derivation.
    const showDerivation = fromCob || hasLtcavPeriodDerivedCap(terms);
    const durationMonths = fromCob
      ? (terms.extensionOfBenefitsPoolDurationMonths ?? DEFAULT_COB_MONTHS)
      : (terms.baseBenefitPoolDurationMonths ?? DEFAULT_LTCAV_MONTHS);
    const scheduledPool = monthlyCap * durationMonths;
    const note = fromCob
      ? 'COB uses the first anniversary step, then later COB inflation, and is available after LTCAV is exhausted.'
      : 'LTCAV follows the guaranteed base-benefit schedule when rate fields are present; legacy rows use the stored LTCAV fallback.';

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        {showDerivation ? (
          <ScheduleLimitTooltip
            title={`Policy year ${intYear} · ${poolLabel} monthly limit`}
            scheduledPool={scheduledPool}
            durationMonths={durationMonths}
            monthlyCap={monthlyCap}
            note={note}
          />
        ) : (
          <TooltipContent className="max-w-xs text-xs">
            <div className="mb-1 font-semibold">
              Policy year {intYear} · {poolLabel} monthly limit
            </div>
            <div className="mb-1 text-[10px] text-gray-300">
              This policy predates the guaranteed-rate extraction, so the monthly limit is the
              policy&apos;s stated monthly benefit rather than a pool ÷ benefit-period derivation.
            </div>
            <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
              <span className="text-gray-300">Monthly cap</span>
              <span className="font-semibold tabular-nums">{currency(monthlyCap)}</span>
              <span className="text-gray-300">Daily equivalent</span>
              <span className="font-semibold tabular-nums">
                {currency(monthlyCap / DAYS_IN_MONTH)}/day
              </span>
            </div>
          </TooltipContent>
        )}
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState, terms }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    const ltcavScheduleNote = ltcavOpeningScheduleNote({ month, openBase, terms });
    const cobScheduleNote = cobOpeningScheduleNote({ month, openEob, terms });

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="Draw order: LTCAV first; COB begins after LTCAV reaches $0."
        sections={[
          {
            heading: 'LTC Accumulated Value (LTCAV)',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Scheduled accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
            note: ltcavScheduleNote,
          },
          {
            heading: 'Continuation of Benefits (COB)',
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
            note: cobScheduleNote,
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const ltcavDrawdown = month.deltas.base.depletion;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? `LTCAV drew ${currency(ltcavDrawdown)}; the Accumulated Value residual was depleted to $0.`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Residual Accumulated Value"
        description="AnnuityCare II's annuity Accumulated Value is reduced proportionally by LTCAV withdrawals only — COB withdrawals do not reduce it. Any Accumulated Value remaining after the claim is paid as a residual non-LTC payout."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced by LTCAV draw', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    if (eligibleDays === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
    const oopOverCap = Math.max(eligibleCost - paid, 0);
    const breakdown = month.events.ineligibleDayBreakdown;
    const oopLt2Adl = breakdown.lt2AdlDays * dailyCost;
    const oopWp = breakdown.inWpDays * dailyCost;
    const oopIl = breakdown.ilNotCoveredDays * dailyCost;
    const oopPrePolicy = breakdown.prePolicyDays * dailyCost;
    const totalOop = oopOverCap + oopLt2Adl + oopWp + oopIl + oopPrePolicy;

    const bothPoolsPaid = drawnBase > 0 && drawnEob > 0;
    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? 'Pays a fixed cash benefit up to the monthly cap regardless of actual cost, LTCAV first then COB. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.'
      : 'Reimburses eligible incurred cost, up to the monthly cap, LTCAV first then COB.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {terms.isIndemnity ? (
            // Indemnity is a cash waterfall, not a day-bucket breakdown — see
            // `indemnityPayoutWaterfall`.
            indemnityPayoutWaterfall(month)
          ) : (
            <>
              <span className="text-gray-300">Care cost</span>
              <span className="font-semibold tabular-nums">{currency(cost)}</span>
              {isPartial && (
                <>
                  <span className="text-gray-300">
                    Eligible ({eligibleDays}/{DAYS_IN_MONTH} days)
                  </span>
                  <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
                </>
              )}
              <span className="text-gray-300">Policy paid</span>
              <span className="font-semibold tabular-nums">−{currency(paid)}</span>
              <span className="text-gray-300">Out of pocket</span>
              <span className="font-semibold tabular-nums text-rose-300">{currency(totalOop)}</span>
              {oopOverCap > 0 && (
                <>
                  <span className="pl-2 text-gray-400">· over cap</span>
                  <span className="tabular-nums text-rose-300">{currency(oopOverCap)}</span>
                </>
              )}
              {oopWp > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · waiting period <span className="text-gray-500">· {breakdown.inWpDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopWp)}</span>
                </>
              )}
              {oopLt2Adl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    {'· <2 ADL'} <span className="text-gray-500">· {breakdown.lt2AdlDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopLt2Adl)}</span>
                </>
              )}
              {oopIl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · independent living
                    <span className="text-gray-500"> · {breakdown.ilNotCoveredDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopIl)}</span>
                </>
              )}
              {oopPrePolicy > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · before policy start
                    <span className="text-gray-500"> · {breakdown.prePolicyDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopPrePolicy)}</span>
                </>
              )}
            </>
          )}
        </div>
        {bothPoolsPaid ? (
          <div className="mt-1 border-t border-gray-700 pt-1">
            <div className="mb-0.5 text-[10px] text-gray-400">
              LTCAV exhausted this month — COB covers the rest:
            </div>
            <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
              <span className="text-gray-300">LTCAV drawn</span>
              <span className="font-semibold tabular-nums">{currency(drawnBase)}</span>
              <span className="text-gray-300">COB drawn</span>
              <span className="font-semibold tabular-nums">{currency(drawnEob)}</span>
            </div>
          </div>
        ) : drawnBase > 0 || drawnEob > 0 ? (
          <div className="mt-1 text-[10px] text-gray-400">
            Drawn from {drawnBase > 0 ? 'LTCAV' : 'COB'}{' '}
            {currency(drawnBase > 0 ? drawnBase : drawnEob)}
          </div>
        ) : null}
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;

    if (!(wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0)) return null;

    return (
      <div className="mt-0.5 text-[10px] text-amber-700">
        {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
      </div>
    );
  },

  // The headline cap follows the COB rate once in the COB phase; the
  // BenefitAmountCell override handles the per-row pool/rate logic.
  benefitInflationRate: terms => terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate,
  poolLimitCell: ({ snapshot, terms }) => (
    <AnnuityCareTwoPoolLimitCell
      snapshot={snapshot}
      terms={terms}
    />
  ),
  drawnCellLabels: {
    description:
      'Cumulative reduction of the AnnuityCare II LTC pools. LTCAV is drawn first; COB is drawn only after LTCAV is exhausted.',
    baseLabel: 'LTCAV drawn',
    cobLabel: 'COB drawn',
  },
  columnLabels: {
    'pool-limit': 'Scheduled pool',
    'death-benefit': 'Residual AV',
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. AnnuityCare II draws from the LTC Accumulated Value (LTCAV) first, then the Continuation of Benefits (COB) Balance once the LTCAV is exhausted. Rate-model rows schedule LTCAV by carrier rates; the COB pool inflates at each policy anniversary while open."
        formulas={[
          {
            label: 'Closing pool',
            expression: '(opening LTCAV + opening COB) + scheduled accrual − drawn this month',
          },
          {
            label: 'Draw order',
            expression: 'LTCAV first; COB engages once the LTCAV is exhausted',
          },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity), with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly cap, pool remaining), LTCAV first then COB',
          },
          {
            label: 'Indemnity mode',
            expression: 'min(monthly cap, pool remaining), LTCAV first then COB',
          },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this policy year, with the daily equivalent beneath. While the LTCAV has balance the cap is scheduled LTCAV ÷ 24 months; once the LTCAV is exhausted the draw moves to COB and uses scheduled COB ÷ 72 months."
        formulas={[
          { label: 'LTCAV phase', expression: 'scheduled LTCAV ÷ 24 months' },
          {
            label: 'COB phase',
            expression: 'scheduled COB ÷ 72 months',
          },
        ]}
        notes="Older policies without detailed rate data fall back to the stored LTCAV and COB inflation, and use the policy's stated monthly limit."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Scheduled LTCAV + COB pool sizes at this policy year if no benefits were used. The LTCAV follows the guaranteed base-benefit rate schedule when available; COB follows its first anniversary step and later COB inflation. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          { label: 'LTCAV', expression: 'scheduled LTCAV' },
          { label: 'COB', expression: 'scheduled COB' },
          { label: 'Total', expression: 'scheduled LTCAV + scheduled COB' },
        ]}
        notes="Older policies without detailed rate data keep the stored LTCAV flat and compound the stored COB amount."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the LTCAV + COB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (LTCAV depletion + COB depletion)' },
          {
            label: 'Identity',
            expression: 'Scheduled pool − Drawn = Pool remaining (while pool is open)',
          },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month residual Accumulated Value — the annuity's account value, reduced on a proportional basis by LTCAV withdrawals only (the AV falls by the same percentage as the LTCAV pool)."
        formulas={[
          {
            label: 'Residual',
            expression: 'open AV × (LTCAV after withdrawal ÷ LTCAV before withdrawal)',
          },
        ]}
        notes="COB withdrawals do not reduce the Accumulated Value. The residual reaches $0 when the LTCAV pool is exhausted, and any amount remaining after the claim is paid as a non-LTC payout."
      />
    ),
    benefitInflationCompoundLabel: 'Compound growth',
    benefitInflationCapNote: capYear => `Inflation capped at year ${capYear}`,
  },
};
